import { DownloadIcon } from "@chakra-ui/icons";
import { Button, Icon } from "@chakra-ui/react";
import { FunctionComponent, useCallback } from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  buildRuleImportErrors,
  downloadRuleConfiguration,
  RuleImportError,
} from "./helpers";

type Props = {
  formTemplateId: string;
  onErrors: (errors: RuleImportError[]) => void;
};

export const ExportRulesButton: FunctionComponent<Props> = ({
  formTemplateId,
  onErrors,
}) => {
  const toast = useAvelaToast();

  const service = FSQ.useExportRuleConfiguration(
    { path: { formTemplateId } },
    ["exportRuleConfiguration", formTemplateId],
    { enabled: false }
  );

  const handleExport = useCallback(async () => {
    const loadingToastId = toast({
      title: "Export in progress",
      description: "Download will start shortly",
      status: "loading",
    });

    try {
      const response = await service.refetch();
      const result = response.data?.data;

      if (!result) {
        throw new Error("No data");
      }

      if (result.result === "invalid") {
        toast.close(loadingToastId);
        toast.error({
          title: "Rules could not be exported",
          description: "Review the specific errors and try again",
        });
        onErrors(buildRuleImportErrors(result.errorGroups));
        return;
      }

      downloadRuleConfiguration(result.rules);
      toast.close(loadingToastId);
      toast({ title: "Rules exported", status: "success" });
    } catch (error) {
      toast.close(loadingToastId);
      toast.genericError();
      console.error(error);
    }
  }, [service, toast, onErrors]);

  return (
    <Button
      variant="solid"
      size="md"
      leftIcon={<Icon as={DownloadIcon} />}
      onClick={handleExport}
    >
      Export JSON
    </Button>
  );
};
